import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import AddButton from "../../components/buttons/button";
import errorToast from "../../components/helper-components/error-toast";
import successToast from "../../components/helper-components/success-toast";
import BulkReturnTable from "../../components/purchase-section/bulk/bulk-return-table";
import BulkEmptyState from "../../components/purchase-section/bulk/bulk-empty-state";
import BulkReturnFooter from "../../components/purchase-section/bulk/bulk-return-footer";
import BulkAllocateSidePanel from "../../components/purchase-section/bulk/bulk-allocate-side-panel";
import {
  purchaseExpiredItemsGetAction,
  purchaseReturnBulkDraftCreateAction,
} from "../../redux/actions/purchase-action";
import { warehouseGetIdAction } from "../../redux/actions/inventory-action";
import { storeIdAction } from "../../redux/actions/general-action";
import { storeDetailsAction } from "../../redux/actions/invoices-action";
import {
  calculateBatchAllocation,
  sumTotalsFromRows,
  buildCmrAllocationPayload,
  buildPurchaseReturnDocuments,
  getRowAllocationSummary,
  round,
  toNumber,
} from "../../utils/purchase-return-calculations";

// Maps one `itemData` entry from /purchaseorder/vendor/getExpired into a
// bulk-return table row. Each batch's qty/rate/discount is pre-computed via
// the shared calculateBatchAllocation formula and defaults to fully
// allocated (qty = total_allocatable_qty), so a freshly loaded row already
// counts toward the footer/table totals — `batches` is the single live
// source of truth, edited directly (no separate draft/commit step).
const mapExpiredItemToRow = (apiItem) => {
  const rawBatches = apiItem?.itemBatchData || [];
  // uom_dropdown entries key their id as `item_id`, not `item_uom_id` — that
  // field only exists at the item level. Using the wrong key here left every
  // option's value undefined (id/label appeared swapped and selection reset
  // to blank, since every option shared that same undefined value).
  const uomOptions = (apiItem?.uom_dropdown || []).map((u) => ({
    value: u.item_id,
    label: u.alt_uom,
    base_quantity: toNumber(u.base_quantity) || 1,
    base_uom_name: u.base_uom?.name || u.sub_uom || "",
  }));
  const itemBaseQuantity = toNumber(apiItem?.base_quantity) || 1;
  const itemBaseUomName =
    uomOptions.find((u) => u.value === apiItem?.item_uom_id)?.base_uom_name || "";

  const batches = rawBatches.map((b, batchIndex) => {
    // itemBatchData entries are bin-location splits of the same batch — the
    // API repeats `total_allocatable_qty` (the batch's grand total) on every
    // split row, so using it directly as each row's own cap double(or more)-
    // counts. `bin_qty` (falling back to item_sellable_quantity) is the real
    // per-row allocatable amount; they sum to the batch's actual total.
    const rowAllocatableQty =
      toNumber(b?.bin_qty) ||
      toNumber(b?.item_sellable_quantity) ||
      toNumber(b?.total_allocatable_qty);
    // manual_discount_amount is the batch's own discount, remembered
    // separately from discount_amount (the active/effective value) so a
    // header discount % can override discount_amount and later "retract"
    // back to whatever was manually set, instead of losing it.
    const manualDiscount = toNumber(b?.item_batch_discount_amount);
    const base = {
      key: `${apiItem?.item_id}-${b?.item_batch_number}-${batchIndex}`,
      item_batch_number: b?.item_batch_number,
      item_exp_date: b?.item_exp_date,
      to_bin_location: b?.to_bin_location,
      to_bin_id: b?.to_bin_id || null,
      unit_price: toNumber(b?.item_batch_unit_price),
      cmr_id: b?.cmr_id,
      cmr_code: b?.cmr_code,
      cmr_name: b?.cmr_name,
      cmr_gstin: b?.cmr_gstin,
      cmr_ship_to_address: b?.cmr_ship_to_address,
      cmr_pay_to_address: b?.cmr_pay_to_address,
      total_allocatable_qty: rowAllocatableQty,
      purchase_rate: toNumber(b?.item_batch_purchase_rate),
      discount_amount: manualDiscount,
      manual_discount_amount: manualDiscount,
      cgst_percentage: toNumber(b?.item_batch_cgst_percentage),
      sgst_percentage: toNumber(b?.item_batch_sgst_percentage),
      igst_percentage: toNumber(b?.item_batch_igst_percentage),
      is_inter_state: !!b?.is_inter_state,
      item_uom_id: apiItem?.item_uom_id,
      item_uom: apiItem?.item_uom,
      base_uom_name: itemBaseUomName,
      base_quantity: itemBaseQuantity,
      qty: rowAllocatableQty,
    };
    return { ...base, ...calculateBatchAllocation(base) };
  });

  const distinctParties = new Set(rawBatches.map((b) => b?.cmr_id));

  return {
    item_id: apiItem?.item_id,
    item_code: apiItem?.item_code,
    item_name: apiItem?.item_name,
    item_generic_name: apiItem?.item_generic_name || "",
    item_hsn: apiItem?.item_hsn,
    item_uom: apiItem?.item_uom,
    item_manufacturer_id: apiItem?.item_manufacturer_id || "",
    item_manufacturer_name: apiItem?.item_manufacturer_name || "",
    item_available_qty: toNumber(apiItem?.total_sellable_quantity),
    item_qty: toNumber(apiItem?.item_expired_quantity),
    no_of_parties: distinctParties.size,
    item_total: toNumber(apiItem?.item_total_amount),
    uom_dropdown: uomOptions,
    batches,
  };
};

// Applies the same qty/rate/discount clamping + recalculation the allocate
// panel used to do locally, but directly onto one batch within `rows` state
// — every edit lands on the row/footer immediately, no separate apply step.
const recalculateBatch = (batch, changes, uomOptions) => {
  let nextChanges = { ...changes };
  if ("qty" in nextChanges) {
    nextChanges.qty = Math.max(
      0,
      Math.min(toNumber(nextChanges.qty), toNumber(batch.total_allocatable_qty))
    );
  }
  if ("purchase_rate" in nextChanges) {
    nextChanges.purchase_rate = Math.max(0, toNumber(nextChanges.purchase_rate));
  }
  if ("discount_amount" in nextChanges) {
    const clamped = Math.max(0, toNumber(nextChanges.discount_amount));
    nextChanges.discount_amount = clamped;
    nextChanges.manual_discount_amount = clamped;
  }
  if ("item_uom_id" in nextChanges) {
    const selected = (uomOptions || []).find((u) => u.value === nextChanges.item_uom_id);
    nextChanges.item_uom = selected?.label || batch.item_uom;
    nextChanges.base_uom_name = selected?.base_uom_name || batch.base_uom_name;
    nextChanges.base_quantity = selected?.base_quantity || 1;
  }
  const next = { ...batch, ...nextChanges };
  return { ...next, ...calculateBatchAllocation(next) };
};

const BulkPurchaseReturn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loadingExpiry, setLoadingExpiry] = useState(false);
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [storeId, setStoreId] = useState(null);
  const [storeState, setStoreState] = useState(null);

  // Same store_id / store_state lookup every other create-return screen
  // does on mount (there's no pre-loaded redux slice for it) — needed for
  // orderData.store_id and for recomputing is_inter_state per group.
  useEffect(() => {
    dispatch(
      storeIdAction(
        (resp) => setStoreId(resp?.data?.store_id || null),
        () => {}
      )
    );
    dispatch(
      storeDetailsAction(
        (resp) => setStoreState(resp?.data?.store_state || null),
        () => {}
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // getExpired is scoped to a warehouse, and there's no pre-loaded redux
  // slice for it — resolve the current warehouse (same warehouseGetId ->
  // pos_id lookup the drop/pick list screens do) before firing the request.
  const handleLoadExpiryItems = (days) => {
    setLoadingExpiry(true);
    dispatch(
      warehouseGetIdAction(
        {},
        (res) => loadExpiryItems(days, res?.data?.pos_id ?? null),
        (err) => {
          setLoadingExpiry(false);
          errorToast(err?.data?.message || "Failed to fetch warehouse");
        }
      )
    );
  };

  const loadExpiryItems = (days, warehouseId) => {
    dispatch(
      purchaseExpiredItemsGetAction(
        { days, warehouse_id: warehouseId },
        (resp) => {
          setLoadingExpiry(false);
          const items = resp?.data?.itemData || [];
          if (items.length === 0) {
            errorToast("No expired items to load");
            return;
          }
          setRows(items.map(mapExpiredItemToRow));
          successToast(resp?.message || "Items loaded");
        },
        (err) => {
          setLoadingExpiry(false);
          errorToast(err?.data?.message || err?.message || "Failed to load expired items");
        }
      )
    );
  };

  const handleAllocate = (rowIndex) => {
    setSelectedRowIndex(rowIndex);
    setAllocateOpen(true);
  };

  const handleUpdateBatch = (batchIndex, changes) => {
    setRows((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== selectedRowIndex) return row;
        const batches = row.batches.map((batch, bi) =>
          bi === batchIndex ? recalculateBatch(batch, changes, row.uom_dropdown) : batch
        );
        return { ...row, batches };
      })
    );
  };

  // Reassigns a batch to a different vendor picked from
  // /purchaseorder/vendor/getVendorsForItemBatch — replaces that vendor's
  // identity, addresses (defaulted from the response; changeable via the
  // reused AddressSidebar), pricing, and allocatable cap in place. Discount
  // resets since a discount negotiated with the old vendor doesn't carry
  // over to the new one.
  const handleChangeVendor = (batchIndex, vendor) => {
    if (!vendor) return;
    setRows((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== selectedRowIndex) return row;
        const batches = row.batches.map((batch, bi) => {
          if (bi !== batchIndex) return batch;
          // getVendorsForItemBatch isn't scoped to a bin location, so
          // vendor.qty_available is the vendor's batch-wide total — not the
          // physical qty sitting in THIS bin-split row. Only let it shrink
          // the cap (a vendor with less outstanding than this bin holds),
          // never raise it above what's physically here.
          const newCap = Math.min(
            toNumber(vendor?.qty_available),
            toNumber(batch.total_allocatable_qty)
          );
          const next = {
            ...batch,
            cmr_id: vendor?.cmr_id,
            cmr_code: vendor?.cmr_code,
            cmr_name: vendor?.cmr_name,
            cmr_gstin: vendor?.cmr_gstin,
            cmr_ship_to_address: vendor?.cmr_ship_to_address,
            cmr_pay_to_address: vendor?.cmr_pay_to_address,
            purchase_rate: toNumber(vendor?.item_batch_purchase_rate),
            cgst_percentage: toNumber(vendor?.item_batch_cgst_percentage),
            sgst_percentage: toNumber(vendor?.item_batch_sgst_percentage),
            igst_percentage: toNumber(vendor?.item_batch_igst_percentage),
            is_inter_state: !!vendor?.is_inter_state,
            total_allocatable_qty: newCap,
            qty: Math.min(toNumber(batch.qty), newCap),
            discount_amount: 0,
            manual_discount_amount: 0,
          };
          return { ...next, ...calculateBatchAllocation(next) };
        });
        return { ...row, batches };
      })
    );
  };


  // The footer's Discount % writes straight into every batch's
  // discount_amount (a real "header discount"), recalculated through the
  // same shared formula as a manual edit — not just an aggregate display
  // adjustment. Clearing it back to 0 restores each batch's own
  // manual_discount_amount rather than zeroing everything out.
  const handleDiscountPercentChange = (pct) => {
    const clampedPct = Math.min(100, Math.max(0, toNumber(pct)));
    setDiscountPercent(clampedPct);
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        batches: (row.batches || []).map((batch) => {
          const discount_amount =
            clampedPct > 0
              ? round((clampedPct / 100) * toNumber(batch.base_amount))
              : toNumber(batch.manual_discount_amount);
          const next = { ...batch, discount_amount };
          return { ...next, ...calculateBatchAllocation(next) };
        }),
      }))
    );
  };

  const handleDelete = (rowIndex) => {
    setRows((prev) => prev.filter((_, index) => index !== rowIndex));
  };

  const totals = sumTotalsFromRows(rows);
  // "Documents" = number of distinct cmr_id + ship-to + pay-to groups the
  // allocated batches will be split into — the same grouping used for save.
  const documentGroups = buildCmrAllocationPayload(rows);
  const selectedRow = selectedRowIndex !== null ? rows[selectedRowIndex] : null;
  const rowsForTable = rows.map((row) => {
    const summary = getRowAllocationSummary(row);
    return {
      ...row,
      allocated_qty: summary.qty,
      required_qty: summary.required,
      // Overrides the static API reference total with the live total derived
      // from this row's current batches, so it always matches what the
      // batch cards and footer show — those are computed the same way.
      item_total: summary.total_amount,
    };
  });

  const handleCreate = () => {
    if (documentGroups.length === 0) {
      errorToast("Allocate at least one item before saving");
      return;
    }
    if (!storeState) {
      errorToast("Please configure store state in store details");
      return;
    }
    const documentDate = new Date().toISOString().slice(0, 10);
    // One /purchaseorder/return/add-shaped {orderData, itemData,
    // itemBatchData} document per customer group — matches exactly what N
    // individually-created purchase returns would look like.
    const documents = buildPurchaseReturnDocuments(rows, {
      storeId,
      storeState,
      documentDate,
    });
    dispatch(
      purchaseReturnBulkDraftCreateAction(
        documents,
        (resp) => {
          successToast(resp?.message || "Purchase Return created");
          navigate("/purchase-return");
        },
        (err) => {
          errorToast(err?.data?.message || err?.message || "Failed to create purchase return");
        }
      )
    );
  };

  return (
    <Formik initialValues={{}} onSubmit={() => {}}>
      <Form className="h-full flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex justify-start first-div flex-wrap flex-none">
            <div className="p-2">
              <AddButton
                onclkFn={() => handleLoadExpiryItems(180)}
                text="Load Expiry Items(180 days)"
                prefixIcon={false}
                loading={loadingExpiry}
              />
            </div>
            <div className="p-2">
              <AddButton
                onclkFn={() => handleLoadExpiryItems(90)}
                text="Load Expiry Items(90 days)"
                prefixIcon={false}
                loading={loadingExpiry}
              />
            </div>
            <div className="p-2">
              <AddButton
                onclkFn={() => handleLoadExpiryItems(30)}
                text="Load Expiry Items(30 days)"
                prefixIcon={false}
                loading={loadingExpiry}
              />
            </div>
            {/* <div className="p-2">
              <AddButton
                onclkFn={() => errorToast("ehehe")}
                text="Load Slow/Non Moving Items"
                prefixIcon={false}
              />
            </div> */}
          </div>

          {rows.length === 0 ? (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <BulkEmptyState />
            </div>
          ) : (
            <div className="flex-1 min-h-0 px-2">
              <BulkReturnTable
                rows={rowsForTable}
                onAllocate={handleAllocate}
                onDelete={handleDelete}
              />
            </div>
          )}
        </div>

        <BulkReturnFooter
          itemsCount={totals.itemsCount}
          documentsCount={documentGroups.length}
          amount={totals.amount}
          discountPercent={discountPercent}
          onDiscountPercentChange={handleDiscountPercentChange}
          onCancel={() => setRows([])}
          onCreate={handleCreate}
        />

        <BulkAllocateSidePanel
          open={allocateOpen}
          setOpen={setAllocateOpen}
          row={selectedRow}
          onUpdateBatch={handleUpdateBatch}
          onChangeVendor={handleChangeVendor}
        />
      </Form>
    </Formik>
  );
};

export default BulkPurchaseReturn;
